/**
 * Bank Account Slide-over Alpine.js Component
 *
 * Displays bank account details, reconciliation stats, and bank entries
 * with tab filtering (unreconciled/reconciled/all) and infinite scroll.
 * Supports inline reconciliation matching for incoming payments.
 */

export function bankAccountSlideover() {
  return {
    // State
    open: false,
    aiPanelOpen: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    // Data
    bankAccount: null,
    stats: null,
    entries: [],

    // Pagination
    page: 1,
    hasMore: false,

    // Tab filter
    activeTab: 'unreconciled',

    // Kebab menu
    menuOpen: false,
    showBackToTop: false,

    // Reconcile modal
    reconcileModalOpen: false,
    reconcileLoading: false,
    reconcileError: null,
    reconcileData: null,
    // Error surfaced under the inline top-match card / inline search result,
    // scoped to the entry it belongs to.
    inlineReconcileError: null,
    inlineReconcileErrorEntryId: null,
    receiptKindPrompt: null,
    statementBalanceInput: '',

    // Import state
    showImportPanel: false,
    importing: false,
    importResult: null,
    importDragOver: false,
    showSkippedDetails: false,
    showErrorDetails: false,

    // Reconciliation detail / unreconcile verdict modal
    reconDetailOpen: false,
    reconDetailLoading: false,
    reconDetail: null,
    reconDetailError: null,
    unreconcileConfirm: false,
    unreconciling: false,
    unreconcileError: null,

    // Reconciliation matching state
    expandedEntryId: null,
    expandedEntryDirection: null,
    matches: [],
    matchesLoading: false,
    reconQuery: '',
    reconResults: [],
    reconLoading: false,
    reconciling: false,
    reconcilingEntryId: null,
    reconDebounceTimer: null,
    _matchFetchGeneration: 0,
    fetchingTopMatches: false,

    // Confirm all matches state
    confirmingAll: false,
    serverMatchCount: null,
    matchPreview: [],
    showConfirmAllModal: false,

    // Pending transactions state: future-dated payments only, which the
    // reconcile walk takes off the bank balance
    pendingTransactions: [],
    pendingTransactionsCount: 0,
    pendingTransactionsTotalPence: 0,
    pendingTransactionsLoading: false,

    // Pending tab: every payment not yet matched to a bank line, whatever its date
    pendingPayments: [],
    pendingPaymentsCount: 0,
    pendingPaymentsTotalPence: 0,
    pendingPaymentsOverdueCount: 0,
    pendingPaymentsOverdueTotalPence: 0,
    pendingPaymentsLoading: false,

    // Entry search state (searching within the bank entries list)
    searchOpen: false,
    searchQuery: '',
    searchMatches: [],
    searchMatchIndex: -1,
    searchLoading: false,
    searchTotalMatches: 0,
    highlightedTxnId: null,
    _searchNavigating: false,

    // Suspense (errors: paid out or received in
    // error, or the leg that puts it right). Context arrives with the
    // suggested matches for the expanded entry; the form lives in its panel.
    suspenseContext: null,
    suspenseForm: null,
    suspenseSaving: false,
    suspenseError: null,

    // Inline search state (for unmatched entries)
    inlineSearchEntryId: null,
    inlineSearchQuery: '',
    inlineSearchResults: [],
    inlineSearchLoading: false,
    inlineSearchDebounceTimer: null,

    // Open the slideover. { import: true } opens it with the Import payments
    // panel already showing, so Today's "Upload today's bank statement" row
    // lands on the drop zone rather than the kebab menu. { tab: 'pending' }
    // opens it on the Pending tab, for Today's payments-not-in-the-bank row.
    openSlideover(detail = {}) {
      const store = Alpine.store?.('slideovers')

      // Bring to front if already open
      if (store?.isOpen?.('bank-account', 'main')) {
        store.bringToFront('bank-account', 'main')
        if (detail?.import) this.openImport()
        if (detail?.tab === 'pending') this.setTab('pending')
        return
      }

      // Get next z-index
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.open = true
      this.activeTab = detail?.tab === 'pending' ? 'pending' : 'unreconciled'
      // Only the default tab falls back to Reconciled when nothing is unreconciled
      this._initialOpen = this.activeTab === 'unreconciled'
      this.menuOpen = false
      this.clearSearch()
      this.searchOpen = false
      this.entries = []
      this.page = 1
      this.hasMore = false
      this.collapseEntry()
      if (detail?.import) this.openImport()
      else this.cancelImport()

      // Register
      if (store?.register) {
        store.register('bank-account', 'main', this)
      }

      this.fetchEntries(true)
      this.fetchServerMatchCount()
      this.fetchPendingTransactions()
    },

    // Close the slideover
    close() {
      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('bank-account', 'main')
      }

      this.menuOpen = false
      this.collapseEntry()
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    // Fetch entries (page 1 or append)
    async fetchEntries(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.entries = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const filter = this.entriesFilter()
        const params = new URLSearchParams()
        if (filter) params.set('filter', filter)
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/bank_account.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.bankAccount = data.bank_account
        this.stats = data.stats

        const augmentEntry = (e) => ({
          ...e,
          topMatch: null,
          topMatchLoading: false
        })

        if (initial) {
          this.entries = data.entries.map(augmentEntry)
        } else {
          this.entries = [...this.entries, ...data.entries.map(augmentEntry)]
        }

        this.hasMore = data.pagination.has_more

        // On initial open, switch to reconciled tab if nothing unreconciled
        if (this._initialOpen) {
          this._initialOpen = false
          if (this.stats && this.stats.unreconciled_count === 0) {
            this.activeTab = 'reconciled'
            return this.fetchEntries(true)
          }
        }

        // Fetch top matches for unreconciled entries
        this.fetchTopMatches()
      } catch (error) {
        console.error('Failed to fetch bank account:', error)
        this.error = 'Failed to load bank account details. Please try again.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    // The bank-entries filter for the active tab. The Pending tab lists
    // payments, not bank entries, so while it is open the entries (and the
    // counts that come with them) are kept on the unreconciled set.
    entriesFilter() {
      if (this.activeTab === 'all') return ''
      if (this.activeTab === 'pending') return 'unreconciled'
      return this.activeTab
    },

    // Load next page
    loadMore() {
      if (this.activeTab === 'pending') return
      if (this.loadingMore || !this.hasMore) return
      this.page++
      this.fetchEntries(false)
    },

    // Silent refresh — keeps existing content visible while fetching updated data
    async refreshEntries() {
      this.error = null

      try {
        const filter = this.entriesFilter()
        const params = new URLSearchParams()
        if (filter) params.set('filter', filter)
        params.set('page', 1)

        const response = await fetch(`/accounting/accounts/bank_account.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const data = await response.json()
        this.bankAccount = data.bank_account
        this.stats = data.stats

        const augmentEntry = (e) => ({
          ...e,
          topMatch: null,
          topMatchLoading: false
        })

        this.entries = data.entries.map(augmentEntry)
        this.page = 1
        this.hasMore = data.pagination.has_more
        this.fetchTopMatches()
      } catch (error) {
        console.error('Failed to refresh bank account:', error)
      }
    },

    // Tab switching — reset and fetch page 1
    setTab(tab) {
      if (tab === this.activeTab) return
      this.collapseEntry()
      this.clearSearch()
      this.searchOpen = false
      this.activeTab = tab
      if (tab === 'pending') {
        this.fetchPendingPayments()
        return
      }
      this.refreshEntries()
    },

    // Infinite scroll handler
    handleScroll(event) {
      this.showBackToTop = window.shouldShowBackToTop(event)

      const el = event.target
      const scrollPosition = el.scrollTop + el.clientHeight
      const threshold = el.scrollHeight - 200

      if (scrollPosition >= threshold && this.hasMore && !this.loadingMore) {
        this.loadMore()
      }
    },

    scrollToTop() {
      window.scrollToTopHelper(this.$refs.scrollContainer)
    },

    // --- Reconciliation matching ---

    // Expand/collapse an entry's matching panel (unreconciled money_in or money_out)
    toggleEntry(entry) {
      // Reconciled rows: incoming receipts open the reconciliation detail /
      // unreconcile verdict modal; outgoing payments jump straight to their
      // remittance (when one exists — landlord/contractor payments only).
      if (entry.reconciled) {
        if (entry.direction === 'money_in' || entry.suspense) {
          this.openReconciliationDetail(entry)
        } else if (entry.remittance_payment_id) {
          this.openRemittance(entry)
        }
        return
      }

      if (this.expandedEntryId === entry.id) {
        this.collapseEntry()
      } else {
        this.expandEntry(entry)
      }
    },

    // Open the matching panel for an unreconciled entry. Returns the
    // suggested-matches fetch so a caller that needs the suspense context
    // (the suspense form) can await it.
    expandEntry(entry) {
      this.expandedEntryId = entry.id
      this.expandedEntryDirection = entry.direction
      this.matches = []
      this.reconQuery = ''
      this.reconResults = []
      this.reconcileError = null
      this.suspenseContext = null
      this.suspenseForm = null
      this.suspenseError = null
      return this.fetchMatches(entry.id)
    },

    // Collapse any expanded entry
    collapseEntry() {
      this.expandedEntryId = null
      this.expandedEntryDirection = null
      this.matches = []
      this.matchesLoading = false
      this.reconQuery = ''
      this.reconResults = []
      this.reconLoading = false
      this.reconciling = false
      this.reconcileError = null
      this.suspenseContext = null
      this.suspenseForm = null
      this.suspenseSaving = false
      this.suspenseError = null
      if (this.reconDebounceTimer) {
        clearTimeout(this.reconDebounceTimer)
        this.reconDebounceTimer = null
      }
    },

    // Fetch suggested matches for a bank entry
    async fetchMatches(entryId) {
      this.matchesLoading = true
      this.matches = []

      try {
        const response = await fetch(`/accounting/accounts/bank_account/matches?bank_entry_id=${entryId}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.matches = data.matches || []
        this.suspenseContext = data.suspense || null
      } catch (error) {
        console.error('Failed to fetch matches:', error)
      } finally {
        this.matchesLoading = false
      }
    },

    // --- Suspense ---

    // Open the suspense form for an entry, expanding its panel first if
    // needed so the open-items context has loaded and can pick the
    // right leg by default (a return, when an open item matches the amount).
    async openSuspenseForm(entry) {
      if (this.expandedEntryId !== entry.id) {
        await this.expandEntry(entry)
      }
      const returnOf = this.suspenseContext?.return_of || null
      this.suspenseForm = {
        entryId: entry.id,
        kind: returnOf ? 'return' : 'error',
        counterparty: (returnOf && returnOf.counterparty) || entry.payee || '',
        note: '',
        returnsBankEntryId: returnOf ? returnOf.bank_entry_id : null
      }
      this.suspenseError = null
    },

    suspenseKindOptions(entry) {
      if (entry.direction === 'money_out') {
        return [
          { value: 'error', label: 'Paid out in error', hint: 'Left the client account by mistake and will be repaid' },
          { value: 'return', label: 'Returning money received in error', hint: 'Sends back a receipt that should never have landed here' }
        ]
      }
      return [
        { value: 'error', label: 'Received in error', hint: 'Landed in the client account by mistake and will be returned' },
        { value: 'return', label: 'Repayment of money paid out in error', hint: 'Puts back what left the client account by mistake' }
      ]
    },

    async confirmSuspense(entry) {
      if (this.suspenseSaving || !this.suspenseForm) return
      const form = this.suspenseForm
      if (!form.counterparty.trim()) {
        this.suspenseError = 'Say who the money went to or came from.'
        return
      }

      this.suspenseSaving = true
      this.suspenseError = null

      try {
        const response = await fetch('/accounting/accounts/bank_account/reconcile_to_suspense', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            bank_entry_id: entry.id,
            kind: form.kind,
            counterparty: form.counterparty.trim(),
            note: form.note.trim(),
            returns_bank_entry_id: form.kind === 'return' ? form.returnsBankEntryId : null
          })
        })

        if (response.status === 409) {
          this.suspenseError = 'This entry has already been reconciled.'
          return
        }
        if (!response.ok) {
          this.suspenseError = await this.reconcileErrorMessage(response)
          return
        }

        const data = await response.json()
        this.applyReconciledEntry(entry.id, data)
        this.collapseEntry()
        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to post to suspense:', error)
        this.suspenseError = 'Couldn’t post this to suspense. Please try again.'
      } finally {
        this.suspenseSaving = false
      }
    },

    // Post the return of an open suspense item straight from its match card:
    // the leg, the counterparty and the line it cancels are all known.
    async confirmSuspenseReturn(entry, match) {
      if (!entry || this.reconcilingEntryId) return
      const item = match.suspense_return_of
      const inline = this.expandedEntryId !== entry.id
      this.reconcilingEntryId = entry.id
      this.reconciling = true
      this.reconcileError = null
      this.inlineReconcileError = null
      this.inlineReconcileErrorEntryId = null

      try {
        const response = await fetch('/accounting/accounts/bank_account/reconcile_to_suspense', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            bank_entry_id: entry.id,
            kind: 'return',
            counterparty: item.counterparty || entry.payee || '',
            returns_bank_entry_id: item.bank_entry_id
          })
        })

        if (!response.ok) {
          const message = response.status === 409
            ? 'This entry has already been reconciled.'
            : await this.reconcileErrorMessage(response)
          if (inline) {
            this.inlineReconcileError = message
            this.inlineReconcileErrorEntryId = entry.id
          } else {
            this.reconcileError = message
          }
          return
        }

        const data = await response.json()
        this.applyReconciledEntry(entry.id, data)
        if (!inline) this.collapseEntry()
        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to post the return to suspense:', error)
        const message = 'Couldn’t post this to suspense. Please try again.'
        if (inline) {
          this.inlineReconcileError = message
          this.inlineReconcileErrorEntryId = entry.id
        } else {
          this.reconcileError = message
        }
      } finally {
        this.reconcilingEntryId = null
        this.reconciling = false
      }
    },

    // Reflect a just-reconciled entry in the list and stats: gone from the
    // unreconciled tab, updated in place elsewhere, and out of confirm-all.
    applyReconciledEntry(entryId, data) {
      if (data.stats) this.stats = data.stats

      const entryIndex = this.entries.findIndex(e => e.id === entryId)
      if (entryIndex !== -1) {
        if (this.activeTab === 'unreconciled') {
          this.entries.splice(entryIndex, 1)
        } else {
          this.entries[entryIndex] = data.entry
        }
      }

      this.matchPreview = this.matchPreview.filter(m => m.bank_entry_id !== entryId)
      this.serverMatchCount = this.matchPreview.length
    },

    // Debounced reconciliation search input handler
    handleReconInput() {
      if (this.reconDebounceTimer) {
        clearTimeout(this.reconDebounceTimer)
      }

      if (this.reconQuery.trim().length < 2) {
        this.reconResults = []
        this.reconLoading = false
        return
      }

      this.reconLoading = true
      this.reconDebounceTimer = setTimeout(() => {
        this.performReconSearch()
      }, 300)
    },

    // Execute reconciliation search
    async performReconSearch() {
      const query = this.reconQuery.trim()
      if (query.length < 2) {
        this.reconResults = []
        this.reconLoading = false
        return
      }

      try {
        let searchUrl = `/accounting/accounts/bank_account/search?q=${encodeURIComponent(query)}`
        if (this.expandedEntryDirection === 'money_out') {
          searchUrl += `&direction=money_out&bank_entry_id=${this.expandedEntryId}`
        }
        const response = await fetch(searchUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.reconResults = data.results || []
      } catch (error) {
        console.error('Failed to search:', error)
        this.reconResults = []
      } finally {
        this.reconLoading = false
      }
    },

    // ===== Landlord receipt kind =====
    // Money credited to a landlord account must be classified for MTD
    // statements: money on account (the landlord's own capital), a deposit
    // award (taxable income), or other income. The prompt pre-selects a
    // suggestion from the bank entry's payee/reference.

    needsReceiptKind(entry, match) {
      const direction = entry?.direction || this.expandedEntryDirection
      return direction !== 'money_out' && match?.entity_type === 'landlord'
    },

    suggestReceiptKind(entry) {
      const text = `${entry?.payee || ''} ${entry?.reference || ''}`
      return /deposit protection|\bdps\b|mydeposits|tenancy deposit scheme|\btds\b/i.test(text)
        ? 'deposit_award'
        : 'money_on_account'
    },

    promptReceiptKind(entry, match, proceed) {
      this.receiptKindPrompt = {
        entityName: match.entity_name,
        kind: this.suggestReceiptKind(entry),
        properties: [],
        propertyId: null,
        proceed
      }
      this.loadReceiptKindProperties(match.account_id)
    },

    // Load the landlord's properties so a deposit award can be attributed to
    // the right one (it groups under that property on statements). Keyed by the
    // account being reconciled to, which every match carries.
    async loadReceiptKindProperties(accountId) {
      if (!accountId) return
      try {
        const response = await fetch(`/accounting/accounts/charges/landlord_properties.json?account_id=${accountId}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        if (response.ok && this.receiptKindPrompt) {
          const props = await response.json()
          this.receiptKindPrompt.properties = props
          if (props.length === 1) this.receiptKindPrompt.propertyId = props[0].id
        }
      } catch (e) {
        console.error('Failed to load landlord properties:', e)
      }
    },

    confirmReceiptKind() {
      const prompt = this.receiptKindPrompt
      if (!prompt) return
      // Property applies to income kinds (deposit award / other income); money
      // on account is the landlord's own capital and isn't property income.
      const propertyId = ['deposit_award', 'other_income'].includes(prompt.kind) ? prompt.propertyId : null
      this.receiptKindPrompt = null
      prompt.proceed(prompt.kind, propertyId)
    },

    cancelReceiptKind() {
      this.receiptKindPrompt = null
    },

    expandedEntry() {
      return this.entries.find(e => e.id === this.expandedEntryId)
    },

    // Confirm reconciliation — accepts accountId (incoming) or paymentId (outgoing).
    // matchObj is the match/result the agent clicked; needed to detect landlord
    // targets so the receipt kind can be asked for first.
    async confirmReconcile(idValue, matchObj = null, receiptKind = null, propertyId = null) {
      if (this.reconciling) return

      // Suspense: the return of an open item is confirmed as it stands (every
      // field is known); the search result opens the form instead, which asks
      // what happened and who the money went to.
      if (matchObj?.entity_type === 'suspense') {
        if (matchObj.suspense_return_of) {
          await this.confirmSuspenseReturn(this.expandedEntry(), matchObj)
        } else {
          this.openSuspenseForm(this.expandedEntry())
        }
        return
      }

      if (!receiptKind && matchObj && this.needsReceiptKind(this.expandedEntry(), matchObj)) {
        this.promptReceiptKind(this.expandedEntry(), matchObj, (kind, propId) => this.confirmReconcile(idValue, matchObj, kind, propId))
        return
      }

      this.reconciling = true
      this.reconcileError = null

      try {
        const isOutgoing = this.expandedEntryDirection === 'money_out'
        const url = isOutgoing
          ? '/accounting/accounts/bank_account/reconcile_payment'
          : '/accounting/accounts/bank_account/reconcile'
        const expectedReceiptId = matchObj?.entity_type === 'expected_deposit_receipt' ? matchObj.expected_deposit_receipt_id : null
        const body = isOutgoing
          ? { bank_entry_id: this.expandedEntryId, payment_id: idValue }
          : { bank_entry_id: this.expandedEntryId, account_id: idValue, ...(expectedReceiptId ? { expected_deposit_receipt_id: expectedReceiptId } : {}), ...(receiptKind ? { receipt_kind: receiptKind } : {}), ...(propertyId ? { property_id: propertyId } : {}) }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(body)
        })

        if (response.status === 409) {
          this.reconcileError = 'This entry has already been reconciled.'
          return
        }

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()

        // Update stats
        if (data.stats) {
          this.stats = data.stats
        }

        // Update the entry in our list to show as reconciled
        const reconciledEntryId = this.expandedEntryId
        const entryIndex = this.entries.findIndex(e => e.id === reconciledEntryId)
        if (entryIndex !== -1) {
          if (this.activeTab === 'unreconciled') {
            // Remove from unreconciled list
            this.entries.splice(entryIndex, 1)
          } else {
            // Update in place
            this.entries[entryIndex] = data.entry
          }
        }

        // Update confirm-all state to reflect this manual confirmation
        this.matchPreview = this.matchPreview.filter(m => m.bank_entry_id !== reconciledEntryId)
        this.serverMatchCount = this.matchPreview.length

        this.collapseEntry()
        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to reconcile:', error)
        this.reconcileError = 'Failed to reconcile. Please try again.'
      } finally {
        this.reconciling = false
      }
    },

    // Display helpers for match confidence
    confidenceLabel(score) {
      if (score >= 70) return 'Strong match'
      if (score >= 40) return 'Good match'
      return 'Possible match'
    },

    confidenceColor(score) {
      if (score >= 70) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      if (score >= 40) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
    },

    // Chip for the kind of thing a suggested match points at. Every match type
    // the suggester can return must be named here — an unknown type used to
    // fall through to "Contractor", which is how a deposit-return match came to
    // be dismissed as a contractor payment.
    matchTypeLabel(type) {
      switch (type) {
        case 'tenancy': return 'Tenant'
        case 'landlord': return 'Landlord'
        case 'agency': return 'Agency'
        case 'tenancy_application': return 'Application'
        case 'expected_deposit_receipt': return 'Deposit return'
        case 'suspense': return 'Suspense'
        default: return 'Contractor'
      }
    },

    matchTypeClass(type) {
      switch (type) {
        case 'tenancy': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
        case 'landlord': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400'
        case 'agency': return 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
        case 'tenancy_application': return 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400'
        case 'expected_deposit_receipt': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
        case 'suspense': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400'
        default: return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
      }
    },

    entityTypeLabel(type) {
      switch (type) {
        case 'tenancy': return 'T'
        case 'landlord': return 'L'
        case 'contractor': return 'C'
        default: return '?'
      }
    },

    entityTypeColor(type) {
      switch (type) {
        case 'tenancy': return 'bg-blue-500'
        case 'landlord': return 'bg-purple-500'
        case 'contractor': return 'bg-orange-500'
        default: return 'bg-gray-500'
      }
    },

    // Fetch top match for all unreconciled entries in batches
    async fetchTopMatches() {
      const generation = ++this._matchFetchGeneration
      this.fetchingTopMatches = true
      const unmatched = this.entries.filter(e => !e.reconciled && !e.topMatch && !e.topMatchLoading)

      const batchSize = 5
      for (let i = 0; i < unmatched.length; i += batchSize) {
        if (this._matchFetchGeneration !== generation) return
        const batch = unmatched.slice(i, i + batchSize)
        await Promise.all(batch.map(entry => this.fetchTopMatch(entry)))
      }

      if (this._matchFetchGeneration === generation) {
        this.fetchingTopMatches = false
      }
    },

    // Fetch top match for a single entry
    async fetchTopMatch(entry) {
      entry.topMatchLoading = true
      try {
        const response = await fetch(`/accounting/accounts/bank_account/matches?bank_entry_id=${entry.id}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        const matches = data.matches || []
        entry.topMatch = matches.length > 0 ? matches[0] : null
      } catch (error) {
        console.error('Failed to fetch top match:', entry.id, error)
      } finally {
        entry.topMatchLoading = false
      }
    },

    // Dismiss inline top match (inline search bar will appear automatically).
    // Persisted server-side so the suggestion doesn't resurface on reopen.
    dismissTopMatch(entry) {
      const match = entry.topMatch
      entry.topMatch = null
      if (!match) return

      const body = { bank_entry_id: entry.id }
      if (match.payment_id) {
        body.payment_id = match.payment_id
      } else if (match.account_id) {
        body.account_id = match.account_id
      } else {
        return
      }

      fetch('/accounting/accounts/bank_account/dismiss_match', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(body)
      }).catch(error => console.error('Failed to dismiss match:', entry.id, error))
    },

    // Inline confirm reconciliation (one-click from entry row)
    async confirmInlineReconcile(entry, receiptKind = null, propertyId = null) {
      if (this.reconcilingEntryId) return
      const match = entry.topMatch
      if (!match) return
      if (match.entity_type === 'suspense') {
        await this.confirmSuspenseReturn(entry, match)
        return
      }

      if (!receiptKind && this.needsReceiptKind(entry, match)) {
        this.promptReceiptKind(entry, match, (kind, propId) => this.confirmInlineReconcile(entry, kind, propId))
        return
      }

      this.reconcilingEntryId = entry.id
      this.inlineReconcileError = null
      this.inlineReconcileErrorEntryId = null

      try {
        const isOutgoing = entry.direction === 'money_out'
        const url = isOutgoing
          ? '/accounting/accounts/bank_account/reconcile_payment'
          : '/accounting/accounts/bank_account/reconcile'
        const idValue = match.payment_id || match.account_id
        const body = isOutgoing
          ? { bank_entry_id: entry.id, payment_id: idValue }
          : { bank_entry_id: entry.id, account_id: idValue, ...(receiptKind ? { receipt_kind: receiptKind } : {}), ...(propertyId ? { property_id: propertyId } : {}) }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(body)
        })

        if (response.status === 409) {
          entry.topMatch = null
          return
        }

        if (!response.ok) throw new Error(await this.reconcileErrorMessage(response))
        const data = await response.json()

        if (data.stats) {
          this.stats = data.stats
        }

        const entryIndex = this.entries.findIndex(e => e.id === entry.id)
        if (entryIndex !== -1) {
          if (this.activeTab === 'unreconciled') {
            this.entries.splice(entryIndex, 1)
          } else {
            this.entries[entryIndex] = { ...data.entry, topMatch: null, topMatchLoading: false }
          }
        }

        // Update confirm-all state to reflect this manual confirmation
        this.matchPreview = this.matchPreview.filter(m => m.bank_entry_id !== entry.id)
        this.serverMatchCount = this.matchPreview.length

        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to reconcile:', error)
        this.inlineReconcileError = error.message
        this.inlineReconcileErrorEntryId = entry.id
      } finally {
        this.reconcilingEntryId = null
      }
    },

    // Pull a human-readable message from a failed reconcile response, falling
    // back to a generic line when the body carries no { error } field.
    async reconcileErrorMessage(response) {
      try {
        const body = await response.json()
        if (body && body.error) return body.error
      } catch (_) { /* non-JSON body */ }
      return 'Couldn’t reconcile this entry. Please try again.'
    },

    // Activate inline search for an entry
    activateInlineSearch(entry) {
      if (this.inlineSearchEntryId === entry.id) return
      this.inlineSearchEntryId = entry.id
      this.inlineSearchQuery = ''
      this.inlineSearchResults = []
      this.inlineSearchLoading = false
      if (this.inlineSearchDebounceTimer) {
        clearTimeout(this.inlineSearchDebounceTimer)
        this.inlineSearchDebounceTimer = null
      }
    },

    // Handle inline search input
    handleInlineSearchInput(event, entry) {
      if (this.inlineSearchEntryId !== entry.id) {
        this.activateInlineSearch(entry)
      }
      this.inlineSearchQuery = event.target.value

      if (this.inlineSearchDebounceTimer) {
        clearTimeout(this.inlineSearchDebounceTimer)
      }

      if (this.inlineSearchQuery.trim().length < 2) {
        this.inlineSearchResults = []
        this.inlineSearchLoading = false
        return
      }

      this.inlineSearchLoading = true
      this.inlineSearchDebounceTimer = setTimeout(() => {
        this.performInlineSearch(entry)
      }, 300)
    },

    // Execute inline search
    async performInlineSearch(entry) {
      const query = this.inlineSearchQuery.trim()
      if (query.length < 2) {
        this.inlineSearchResults = []
        this.inlineSearchLoading = false
        return
      }

      try {
        let searchUrl = `/accounting/accounts/bank_account/search?q=${encodeURIComponent(query)}`
        if (entry.direction === 'money_out') {
          searchUrl += `&direction=money_out&bank_entry_id=${entry.id}`
        }
        const response = await fetch(searchUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        this.inlineSearchResults = data.results || []
      } catch (error) {
        console.error('Failed to search:', error)
        this.inlineSearchResults = []
      } finally {
        this.inlineSearchLoading = false
      }
    },

    // Confirm reconciliation from inline search result
    async confirmInlineSearchReconcile(entry, result, receiptKind = null, propertyId = null) {
      if (result.entity_type === 'suspense') {
        this.openSuspenseForm(entry)
        return
      }
      if (this.reconcilingEntryId) return

      if (!receiptKind && this.needsReceiptKind(entry, result)) {
        this.promptReceiptKind(entry, result, (kind, propId) => this.confirmInlineSearchReconcile(entry, result, kind, propId))
        return
      }

      this.reconcilingEntryId = entry.id
      this.inlineReconcileError = null
      this.inlineReconcileErrorEntryId = null

      try {
        const isOutgoing = entry.direction === 'money_out'
        const url = isOutgoing
          ? '/accounting/accounts/bank_account/reconcile_payment'
          : '/accounting/accounts/bank_account/reconcile'
        const idValue = result.payment_id || result.account_id
        const body = isOutgoing
          ? { bank_entry_id: entry.id, payment_id: idValue }
          : { bank_entry_id: entry.id, account_id: idValue, ...(receiptKind ? { receipt_kind: receiptKind } : {}), ...(propertyId ? { property_id: propertyId } : {}) }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(body)
        })

        if (response.status === 409) return
        if (!response.ok) throw new Error(await this.reconcileErrorMessage(response))
        const data = await response.json()

        if (data.stats) {
          this.stats = data.stats
        }

        const entryIndex = this.entries.findIndex(e => e.id === entry.id)
        if (entryIndex !== -1) {
          if (this.activeTab === 'unreconciled') {
            this.entries.splice(entryIndex, 1)
          } else {
            this.entries[entryIndex] = { ...data.entry, topMatch: null, topMatchLoading: false }
          }
        }

        // Update confirm-all state to reflect this manual confirmation
        this.matchPreview = this.matchPreview.filter(m => m.bank_entry_id !== entry.id)
        this.serverMatchCount = this.matchPreview.length

        // Clear inline search state
        this.inlineSearchEntryId = null
        this.inlineSearchQuery = ''
        this.inlineSearchResults = []

        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to reconcile:', error)
        this.inlineReconcileError = error.message
        this.inlineReconcileErrorEntryId = entry.id
      } finally {
        this.reconcilingEntryId = null
      }
    },

    // Bar color based on reconciliation status and match quality
    barColorClass(entry) {
      if (entry.reconciled) return 'bg-emerald-400'
      if (entry.topMatch && entry.topMatch.score >= 70) return 'bg-emerald-400'
      return 'bg-amber-400'
    },

    // Fetch the total match count and preview from the server
    async fetchServerMatchCount() {
      this.serverMatchCount = null
      this.matchPreview = []
      try {
        const response = await fetch('/accounting/accounts/bank_account/match_count', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        this.serverMatchCount = data.match_count
        this.matchPreview = data.matches || []
      } catch (error) {
        console.error('Failed to fetch match count:', error)
        this.serverMatchCount = 0
        this.matchPreview = []
      }
    },

    get strongMatchCount() {
      return this.matchPreview.filter(m => m.score >= 70).length
    },

    get otherMatchCount() {
      return this.matchPreview.filter(m => m.score < 70).length
    },

    get matchPreviewTotal() {
      return this.matchPreview.reduce((sum, m) => sum + m.amount_pence, 0)
    },

    // Show the confirmation modal
    confirmAllMatches() {
      if (this.matchPreview.length === 0) return
      this.showConfirmAllModal = true
    },

    // Remove a match from the preview list
    removeFromPreview(bankEntryId) {
      this.matchPreview = this.matchPreview.filter(m => m.bank_entry_id !== bankEntryId)
      if (this.matchPreview.length === 0) {
        this.showConfirmAllModal = false
      }
    },

    // Actually execute bulk reconciliation after modal confirmation
    async executeConfirmAll() {
      if (this.confirmingAll) return
      this.confirmingAll = true

      try {
        const entryIds = this.matchPreview.map(m => m.bank_entry_id)
        const response = await fetch('/accounting/accounts/bank_account/reconcile_all', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ bank_entry_ids: entryIds })
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()

        if (data.stats) {
          this.stats = data.stats
        }

        this.showConfirmAllModal = false

        // Refresh the entries list and match count
        this.fetchEntries(true)
        this.fetchServerMatchCount()
        this.fetchPendingTransactions()
      } catch (error) {
        console.error('Failed to reconcile all:', error)
      } finally {
        this.confirmingAll = false
      }
    },

    isExpandable(entry) {
      return !entry.reconciled
    },

    // Rows that respond to a click: unreconciled entries (inline matching),
    // reconciled incoming receipts (unreconcile verdict modal), and reconciled
    // outgoing payments that have a remittance to open.
    isRowClickable(entry) {
      return this.isExpandable(entry)
        || (entry.reconciled && entry.direction === 'money_in')
        || (entry.reconciled && !!entry.suspense)
        || (entry.reconciled && entry.direction === 'money_out' && !!entry.remittance_payment_id)
    },

    // Open the remittance produced for a reconciled outgoing payment.
    openRemittance(entry) {
      window.open(
        `/accounting/accounts/payments/${entry.remittance_payment_id}/remittance.pdf`,
        '_blank',
        'noopener'
      )
    },

    // --- Reconciliation detail (unreconcile verdict) ---

    async openReconciliationDetail(entry) {
      this.reconDetailOpen = true
      this.reconDetailLoading = true
      this.reconDetail = null
      this.reconDetailError = null
      this.unreconcileConfirm = false
      this.unreconciling = false
      this.unreconcileError = null
      try {
        const response = await fetch(
          `/accounting/accounts/bank_account/reconciliation_detail.json?bank_entry_id=${entry.id}`,
          {
            headers: {
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
          }
        )
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.reconDetail = await response.json()
      } catch (error) {
        console.error('Failed to load reconciliation detail:', error)
        this.reconDetailError = 'Could not load this reconciliation.'
      } finally {
        this.reconDetailLoading = false
      }
    },

    closeReconciliationDetail() {
      this.reconDetailOpen = false
      this.reconDetail = null
      this.reconDetailError = null
      this.unreconcileConfirm = false
      this.unreconciling = false
      this.unreconcileError = null
    },

    // Undo the reconciliation (two-step: arm the confirm, then perform).
    async performUnreconcile() {
      if (!this.reconDetail) return
      this.unreconciling = true
      this.unreconcileError = null
      try {
        const response = await fetch('/accounting/accounts/bank_account/unreconcile.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ bank_entry_id: this.reconDetail.bank_entry.id })
        })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Could not unreconcile this entry.')

        this.closeReconciliationDetail()
        this.refreshEntries()
      } catch (error) {
        this.unreconcileError = error.message
        this.unreconcileConfirm = false
      } finally {
        this.unreconciling = false
      }
    },

    // --- Pending transactions ---

    async fetchPendingTransactions() {
      // Every place that refreshes the future-dated list (open, payments made,
      // reconciling, importing) refreshes the Pending tab with it.
      this.fetchPendingPayments()
      this.pendingTransactionsLoading = true
      try {
        const response = await fetch('/accounting/accounts/bank_account/pending_transactions.json', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        this.pendingTransactions = data.payments || []
        this.pendingTransactionsCount = data.count || 0
        this.pendingTransactionsTotalPence = data.total_pence || 0
      } catch (error) {
        console.error('Failed to fetch pending transactions:', error)
        this.pendingTransactions = []
        this.pendingTransactionsCount = 0
        this.pendingTransactionsTotalPence = 0
      } finally {
        this.pendingTransactionsLoading = false
      }
    },

    async fetchPendingPayments() {
      this.pendingPaymentsLoading = true
      try {
        const response = await fetch('/accounting/accounts/bank_account/pending_payments.json', {
          headers: { 'Accept': 'application/json' }
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        this.pendingPayments = data.payments || []
        this.pendingPaymentsCount = data.count || 0
        this.pendingPaymentsTotalPence = data.total_pence || 0
        this.pendingPaymentsOverdueCount = data.overdue_count || 0
        this.pendingPaymentsOverdueTotalPence = data.overdue_total_pence || 0
      } catch (error) {
        console.error('Failed to fetch pending payments:', error)
      } finally {
        this.pendingPaymentsLoading = false
      }
    },

    // "Made by Lyn Lainchbury · 18 Sep 2026, 16:13 · BACS"
    pendingPaymentMadeLine(payment) {
      const parts = []
      if (payment.made_by) parts.push('Made by ' + payment.made_by)
      if (payment.made_at) {
        const at = new Date(payment.made_at)
        parts.push(
          at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
          at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        )
      }
      if (payment.method) parts.push(payment.method === 'bacs' ? 'BACS' : 'Direct')
      return parts.join(' · ')
    },

    // Reconciled from the upload the working day after the payment date
    pendingPaymentExpectedLine(payment) {
      const by = this.formatDate(payment.expected_by)
      return payment.overdue ? `Should have been reconciled by ${by}` : `Should be reconciled by ${by}`
    },

    payableTypeLabel(type) {
      switch (type) {
        case 'Landlord': return 'Landlord'
        case 'Contractor': return 'Contractor'
        case 'Agency': return 'Agency'
        default: return type
      }
    },

    payableTypeBadgeClass(type) {
      switch (type) {
        case 'Landlord': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
        case 'Contractor': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
        case 'Agency': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
        default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
      }
    },

    // --- End pending transactions ---

    // --- End reconciliation matching ---

    // --- Import ---

    openImport() {
      this.showImportPanel = true
      this.importing = false
      this.importResult = null
      this.importDragOver = false
      this.showSkippedDetails = false
      this.showErrorDetails = false
    },

    cancelImport() {
      this.showImportPanel = false
      this.importing = false
      this.importResult = null
      this.importDragOver = false
      this.showSkippedDetails = false
      this.showErrorDetails = false
    },

    handleFileDrop(event) {
      event.preventDefault()
      this.importDragOver = false
      const file = event.dataTransfer?.files?.[0]
      if (file) this.uploadFile(file)
    },

    handleFileSelect(event) {
      const file = event.target?.files?.[0]
      if (file) this.uploadFile(file)
    },

    async uploadFile(file) {
      if (this.importing) return

      this.importing = true
      this.importResult = null

      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch('/accounting/accounts/bank_account/import', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        const data = await response.json()

        if (!response.ok) {
          this.importResult = { success: false, error: data.error || 'Import failed' }
          return
        }

        this.importResult = {
          success: true,
          imported: data.imported,
          skipped: data.skipped,
          skipped_entries: data.skipped_entries || [],
          errors: data.errors || [],
          total: data.total
        }
        this.showSkippedDetails = false
        this.showErrorDetails = false

        // Update stats immediately
        if (data.stats) {
          this.stats = data.stats
        }

        // Refresh entries if any were imported
        if (data.imported > 0) {
          this.fetchEntries(true)
          this.fetchServerMatchCount()
          this.fetchPendingTransactions()
        }
      } catch (error) {
        console.error('Failed to import:', error)
        this.importResult = { success: false, error: 'Failed to upload file. Please try again.' }
      } finally {
        this.importing = false
      }
    },

    // --- End import ---

    // --- Reconcile ---

    async openReconcile() {
      this.reconcileModalOpen = true
      this.reconcileLoading = true
      this.reconcileError = null
      this.reconcileData = null
      this.statementBalanceInput = ''

      // Make sure pending (uncleared) payments are current — the reconciliation
      // walk subtracts them to reveal the true unexplained difference.
      this.fetchPendingTransactions()

      try {
        const response = await fetch('/accounting/accounts/bank_account/reconciliation', {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()
        if (!response.ok) {
          this.reconcileError = data.error || 'Failed to load breakdown'
          return
        }
        this.reconcileData = data
      } catch (error) {
        console.error('Failed to load reconciliation breakdown:', error)
        this.reconcileError = 'Failed to load breakdown. Please try again.'
      } finally {
        this.reconcileLoading = false
      }
    },

    get statementBalancePence() {
      const raw = (this.statementBalanceInput || '').toString().replace(/[£,\s]/g, '')
      if (raw === '' || raw === '-' || raw === '.') return null
      const num = Number(raw)
      if (!Number.isFinite(num)) return null
      return Math.round(num * 100)
    },

    // Live-format the real bank balance as it's typed so it reads like the
    // ledger figure beside it: thousands separators in the pounds, at most two
    // decimal places, and the caret kept where the user left it.
    formatStatementBalanceInput(el) {
      const raw = el.value
      const caret = el.selectionStart ?? raw.length
      const significantBefore = raw.slice(0, caret).replace(/[^\d.-]/g, '').length

      const formatted = this.formatBalanceString(raw)
      if (formatted === raw) return

      let pos = 0
      let seen = 0
      while (pos < formatted.length && seen < significantBefore) {
        if (/[\d.-]/.test(formatted[pos])) seen++
        pos++
      }

      el.value = formatted
      this.statementBalanceInput = formatted
      el.setSelectionRange(pos, pos)
    },

    formatBalanceString(raw) {
      const negative = raw.trim().startsWith('-')
      const cleaned = raw.replace(/[^\d.]/g, '')
      const dot = cleaned.indexOf('.')
      let intPart = dot === -1 ? cleaned : cleaned.slice(0, dot)
      const fracPart = dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2)
      intPart = intPart.replace(/^0+(?=\d)/, '')
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return (negative ? '-' : '') + grouped + (fracPart === null ? '' : '.' + fracPart)
    },

    // On blur, settle a valid figure to the full two decimal places
    // (16,659.6 -> 16,659.60) so it matches the ledger box exactly.
    normaliseStatementBalanceInput() {
      const pence = this.statementBalancePence
      if (pence === null) return
      const pounds = (Math.abs(pence) / 100).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      this.statementBalanceInput = (pence < 0 ? '-' : '') + pounds
    },

    get variancePence() {
      if (this.statementBalancePence === null || !this.reconcileData) return 0
      return this.statementBalancePence - this.reconcileData.ledger_balance_pence
    },

    // Pending payments are already credited out of the LetAdmin ledger but
    // haven't yet left the real bank — so the bank looks higher by this amount.
    get pendingOutflowPence() {
      return this.pendingTransactionsTotalPence || 0
    },

    // What the bank balance will be once the pending payments clear.
    get expectedClearedBalancePence() {
      if (this.statementBalancePence === null) return null
      return this.statementBalancePence - this.pendingOutflowPence
    },

    // The real discrepancy: variance once pending (uncleared) payments are removed.
    // Positive = money in the bank not yet recorded in LetAdmin.
    // Negative = recorded in LetAdmin but missing from the bank.
    get unexplainedPence() {
      if (this.statementBalancePence === null) return 0
      return this.variancePence - this.pendingOutflowPence
    },

    // Payments out of the ledger that are on no bank statement. Shown beside
    // the difference, never taken off it: once one is re-sent the money has
    // left the bank before LetAdmin can see it, so subtracting would invent a
    // shortfall until the next statement is matched.
    get missingPayments() {
      return this.reconcileData?.missing_payments || []
    },

    get missingPaymentsTotalPence() {
      return this.missingPayments.reduce((sum, p) => sum + p.amount_pence, 0)
    },

    get missingPaymentsTitle() {
      const n = this.missingPayments.length
      return n === 1 ? "1 payment hasn't reached the bank" : `${n} payments haven't reached the bank`
    },

    // What the difference would be if the missing payments were never sent
    get leftUnexplainedPence() {
      return this.unexplainedPence - this.missingPaymentsTotalPence
    },

    viewPendingPayments() {
      this.reconcileModalOpen = false
      this.setTab('pending')
    },

    get reconcileMessage() {
      const diff = this.unexplainedPence
      if (diff === 0) return "Everything's accounted for — your bank and LetAdmin agree."
      if (diff > 0) return this.formatPence(diff) + ' is in your bank but not yet recorded in LetAdmin (e.g. a receipt that has just landed).'
      return this.formatPence(Math.abs(diff)) + ' is recorded in LetAdmin but missing from your bank — worth investigating.'
    },

    // --- End reconcile ---

    // --- Entry search (searching within bank entries list) ---

    toggleSearch() {
      this.searchOpen = !this.searchOpen
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus())
      } else {
        this.clearSearch()
      }
    },

    clearSearch() {
      this.searchQuery = ''
      this.searchMatches = []
      this.searchMatchIndex = -1
      this.searchLoading = false
      this.searchTotalMatches = 0
      this.highlightedTxnId = null
      this._searchNavigating = false
    },

    async performSearch() {
      const query = this.searchQuery.trim()
      if (!query) {
        this.searchMatches = []
        this.searchMatchIndex = -1
        this.highlightedTxnId = null
        return
      }

      this.searchLoading = true

      try {
        const filter = this.entriesFilter()
        const params = new URLSearchParams({ query })
        if (filter) params.set('filter', filter)

        const response = await fetch(`/accounting/accounts/bank_account/search_entries?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.searchMatches = data.matches || []
        this.searchTotalMatches = data.total_matches || 0

        if (this.searchMatches.length > 0) {
          this.searchMatchIndex = 0
          this.navigateToMatch(0)
        } else {
          this.searchMatchIndex = -1
          this.highlightedTxnId = null
        }
      } catch (error) {
        console.error('Entry search failed:', error)
        this.searchMatches = []
        this.searchMatchIndex = -1
      } finally {
        this.searchLoading = false
      }
    },

    nextMatch() {
      if (this.searchMatches.length === 0) return
      const next = (this.searchMatchIndex + 1) % this.searchMatches.length
      this.navigateToMatch(next)
    },

    prevMatch() {
      if (this.searchMatches.length === 0) return
      const prev = (this.searchMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length
      this.navigateToMatch(prev)
    },

    async navigateToMatch(index) {
      if (index < 0 || index >= this.searchMatches.length) return

      this.searchMatchIndex = index
      const match = this.searchMatches[index]
      this.highlightedTxnId = match.id

      const container = this.$refs.scrollContainer || this.$el
      const el = container.querySelector(`[data-txn-id="${match.id}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      // Entry not loaded yet — load pages until it appears
      this._searchNavigating = true
      let attempts = 0
      while (this.hasMore && attempts < 20) {
        attempts++
        this.page++
        await this.fetchEntries(false)
        await this.$nextTick()
        const found = container.querySelector(`[data-txn-id="${match.id}"]`)
        if (found) {
          found.scrollIntoView({ behavior: 'smooth', block: 'center' })
          this._searchNavigating = false
          return
        }
      }
      this._searchNavigating = false
    },

    // --- End entry search ---

    // Dynamic text size for stat cards — driven by the longest value
    get statValueClass() {
      if (!this.stats) return 'text-2xl'
      const lengths = [
        String(this.stats.unreconciled_count).length,
        String(this.stats.reconciled_count).length,
        this.formatPence(this.stats.unreconciled_in_pence).length,
        this.formatPence(this.stats.unreconciled_out_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    // Tab counts (from stats, always accurate)
    get unreconciledCount() {
      return this.stats?.unreconciled_count ?? 0
    },

    get reconciledCount() {
      return this.stats?.reconciled_count ?? 0
    },

    get totalCount() {
      return this.stats?.total_count ?? 0
    },

    // Format helpers
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return '\u00A3' + formatted
    },

    formatSignedPence(pence, direction) {
      if (pence === null || pence === undefined) return '-'
      const prefix = direction === 'money_in' ? '+' : '-'
      return prefix + this.formatPence(pence)
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    directionLabel(direction) {
      return direction === 'money_in' ? 'INCOMING' : 'OUTGOING'
    },

    entryDisplayName(entry) {
      return entry.payee || 'Unknown'
    },

    entryReference(entry) {
      return entry.reference || ''
    }
  }
}
